import React, { useState } from 'react';
import api from '../utils/api';
import styled from 'styled-components';
import theme from '../theme';
import { TerminalCard, TerminalButton, TerminalInput, TerminalTitle, TerminalText } from './shared/TerminalBox';
import { TerminalSelect, TerminalLabel, TerminalAlert, TerminalLoading, TerminalTable, TerminalTableHeader, TerminalTableHeaderCell, TerminalTableRow, TerminalTableCell, TerminalCheckbox, TerminalCheckboxLabel } from './shared/TerminalForms';


const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 100vh;
  background: ${theme.colors.bg};
  color: ${theme.colors.text};
`;

const Title = styled(TerminalTitle)`
  margin-bottom: 2rem;
  font-size: 2rem;
  
  &::before {
    content: '> ';
    color: ${theme.colors.primary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ResultsCard = styled(TerminalCard)`
  margin-top: 2rem;
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${theme.colors.border};
`;

const ResultsTitle = styled.h2`
  font-family: ${theme.fonts.mono};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &::before {
    content: '[ ';
    color: ${theme.colors.primary};
  }
  
  &::after {
    content: ' ]';
    color: ${theme.colors.primary};
  }
`;

const ResultsInfo = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${theme.colors.bgSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 600;
  color: ${theme.colors.text};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 1rem;
`;

const Table = styled(TerminalTable)`
  width: 100%;
  min-width: 800px;
`;

const TableHeader = styled(TerminalTableHeader)``;

const TableHeaderCell = styled(TerminalTableHeaderCell)`
  text-align: ${props => props.align || 'left'};
`;

const TableRow = styled(TerminalTableRow)``;

const TableCell = styled(TerminalTableCell)`
  text-align: ${props => props.align || 'left'};
  font-family: ${theme.fonts.mono};
  
  &.symbol {
    font-weight: 700;
    color: ${theme.colors.primary};
  }
  
  &.number {
    text-align: right;
    font-family: ${theme.fonts.mono};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const PageButton = styled(TerminalButton)`
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  font-family: ${theme.fonts.mono};
  font-weight: 600;
  color: ${theme.colors.text};
  font-size: 0.875rem;
`;

const Screeners = () => {
  const [screenerType, setScreenerType] = useState('stock');
  const [fields, setFields] = useState(['close', 'volume', 'market_cap']);
  const [exchanges, setExchanges] = useState(['NYSE', 'NASDAQ']);
  const [countries, setCountries] = useState(['US']);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('market_cap');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const availableFields = {
    stock: ['close', 'volume', 'market_cap', 'name', 'symbol_code', 'country', 'delay_seconds', 'fundamental_currency_code'],
    crypto: ['close', 'volume', 'market_cap', 'name', 'symbol_code', 'delay_seconds', 'fundamental_currency_code']
  };

  const availableExchanges = {
    stock: ['NYSE', 'NASDAQ', 'AMEX', 'TSX', 'LSE', 'TSE']
  };

  const availableCountries = ['US', 'CA', 'GB', 'JP', 'DE', 'FR', 'AU', 'CH'];

  const handleScreener = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        fields: fields,
        page: page,
        sortBy: sortBy,
        sortOrder: sortOrder
      };

      if (screenerType === 'stock') {
        payload.exchanges = exchanges;
      }

      if (screenerType === 'stock' || screenerType === 'bond') {
        payload.countries = countries;
      }

      const response = await api.post('/${screenerType}-screener',
        payload
      );

      setResults(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'An error occurred while screening');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const formatVolume = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toLocaleString();
  };

  return (
    <Container>
      <Title>Market Screeners</Title>

      <TerminalCard>
        <FormGrid>
          <FormGroup>
            <TerminalLabel>Screener Type</TerminalLabel>
            <TerminalSelect
              value={screenerType}
              onChange={(e) => setScreenerType(e.target.value)}
            >
              <option value="stock">Stocks</option>
              <option value="crypto">Cryptocurrencies</option>
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Sort By</TerminalLabel>
            <TerminalSelect
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {availableFields[screenerType].map(field => (
                <option key={field} value={field}>
                  {field.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Sort Order</TerminalLabel>
            <TerminalSelect
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </TerminalSelect>
          </FormGroup>

          <FormGroup>
            <TerminalLabel>Page</TerminalLabel>
            <TerminalInput
              type="number"
              value={page}
              onChange={(e) => setPage(parseInt(e.target.value) || 1)}
              min="1"
            />
          </FormGroup>
        </FormGrid>

        {(screenerType === 'stock') && (
          <FormGroup>
            <TerminalLabel>Exchanges</TerminalLabel>
            <CheckboxGroup>
              {availableExchanges[screenerType].map(exchange => (
                <TerminalCheckboxLabel key={exchange}>
                  <TerminalCheckbox
                    type="checkbox"
                    checked={exchanges.includes(exchange)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setExchanges([...exchanges, exchange]);
                      } else {
                        setExchanges(exchanges.filter(ex => ex !== exchange));
                      }
                    }}
                  />
                  {exchange}
                </TerminalCheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormGroup>
        )}

        {(screenerType === 'stock' || screenerType === 'bond') && (
          <FormGroup>
            <TerminalLabel>Countries</TerminalLabel>
            <CheckboxGroup>
              {availableCountries.map(country => (
                <TerminalCheckboxLabel key={country}>
                  <TerminalCheckbox
                    type="checkbox"
                    checked={countries.includes(country)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCountries([...countries, country]);
                      } else {
                        setCountries(countries.filter(c => c !== country));
                      }
                    }}
                  />
                  {country}
                </TerminalCheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormGroup>
        )}

        <TerminalButton
          onClick={handleScreener}
          disabled={loading}
          style={{ width: '100%', marginTop: '1rem' }}
        >
          {loading ? 'Screening...' : `Screen ${screenerType.charAt(0).toUpperCase() + screenerType.slice(1)}s`}
        </TerminalButton>
      </TerminalCard>

      {error && (
        <TerminalAlert className="error" style={{ marginTop: '1rem' }}>
          {error}
        </TerminalAlert>
      )}

      {loading && (
        <TerminalLoading>
          Screening {screenerType} data...
        </TerminalLoading>
      )}

      {results && (
        <ResultsCard>
          <ResultsHeader>
            <ResultsTitle>
              {screenerType.charAt(0).toUpperCase() + screenerType.slice(1)} Screening Results
            </ResultsTitle>
            <ResultsInfo>
              <StatItem>
                Total: {results.current_items || 0}
              </StatItem>
              <StatItem>
                Page: {results.current_page || 1}
              </StatItem>
              <StatItem>
                Pages: {results.total_page || 1}
              </StatItem>
            </ResultsInfo>
          </ResultsHeader>

          <TableWrapper>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Symbol</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  {fields.includes('close') && <TableHeaderCell align="right">Close</TableHeaderCell>}
                  {fields.includes('volume') && <TableHeaderCell align="right">Volume</TableHeaderCell>}
                  {fields.includes('market_cap') && <TableHeaderCell align="right">Market Cap</TableHeaderCell>}
                  {fields.includes('country') && <TableHeaderCell>Country</TableHeaderCell>}
                  {fields.includes('delay_seconds') && <TableHeaderCell align="right">Delay (s)</TableHeaderCell>}
                </tr>
              </TableHeader>
              <tbody>
                {results.data && results.data.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="symbol">
                      {item.symbol_code}
                    </TableCell>
                    <TableCell title={item.name}>
                      {item.name}
                    </TableCell>
                    {fields.includes('close') && (
                      <TableCell className="number">
                        {item.close ? formatNumber(item.close) : 'N/A'}
                      </TableCell>
                    )}
                    {fields.includes('volume') && (
                      <TableCell className="number">
                        {item.volume ? formatVolume(item.volume) : 'N/A'}
                      </TableCell>
                    )}
                    {fields.includes('market_cap') && (
                      <TableCell className="number">
                        {item.market_cap ? formatNumber(item.market_cap) : 'N/A'}
                      </TableCell>
                    )}
                    {fields.includes('country') && (
                      <TableCell>
                        {item.country || 'N/A'}
                      </TableCell>
                    )}
                    {fields.includes('delay_seconds') && (
                      <TableCell className="number">
                        {item.delay_seconds || 'N/A'}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </TableWrapper>

          <Pagination>
            <PageButton
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || loading}
            >
              Previous
            </PageButton>
            <PageInfo>
              Page {page} of {results.total_page || 1}
            </PageInfo>
            <PageButton
              onClick={() => setPage(page + 1)}
              disabled={!results.hasNext || loading}
            >
              Next
            </PageButton>
          </Pagination>
        </ResultsCard>
      )}
    </Container>
  );
};

export default Screeners;
